import React, { useState } from 'react';
import clsx from 'clsx';
import { FormSection } from './FormSection';
import Dropdown from '../../../_custom/Dropdown';
import Button from '../../../_custom/Button/Button';
import { Input } from '../../../_custom';
import { numberOnly } from '../../../../utils/utils';
import { BLACK_DROPDOWN_CARET, user_outline } from '../../../../assets/hostedassets';
import {
    MANDATE_APPLICANT_FIELD_KEYS as FIELD_KEYS,
    MANDATE_APPLICANT_FIELD_ORDER,
    MANDATE_APPLICANT_LABELS as LABELS,
    MANDATE_APPLICANT_PHONE_COUNTRY_CODE_PREFIX,
    MANDATE_APPLICANT_PHONE_DIGIT_LENGTH,
    MANDATE_APPLICANT_REMOVE_LABEL,
    MANDATE_APPLICANT_TYPE_KEY,
    MANDATE_APPLICANT_TYPE_OPTIONS,
    PINCODE_DIGIT_LENGTH,
} from '../../DealDrawerConstants';
import {
    ApplicantDirectorLink,
    READ_ONLY_FIELD_KEYS_BY_EDIT_STATE,
    isApplicantEmailValid,
    isApplicantPhoneValid,
    isApplicantPincodeValid,
    resolveApplicantEditState,
} from './coApplicantTypes';
import { FormSectionPassThroughProps } from './formSectionPassThroughTypes';
import styles from '../../ManageDealDrawer.module.scss';

interface ApplicantFieldsBlockProps extends FormSectionPassThroughProps, ApplicantDirectorLink {
    /** `primaryApplicant` for the Applicant block, or the co-applicant row id for a Co-applicant row. */
    sectionKey: string;
    /** The shared `primaryApplicant` section's field-config map (same object for the applicant and every co-applicant row). */
    applicantConfig: Record<string, any>;
    formData: Record<string, any>;
    handleChange: (key: string, value: any, importanceKey?: string | null) => void;
    /** Renders the card's Remove action when present; omitted for a row that cannot be removed. */
    onRemove?: () => void;
}

// FormSection owns one combined required-field check per section it renders; this block splits
// the config-driven fields into two FormSections (around Phone/Email, which have no backend
// config entry) purely for field ORDER, so neither half's own gating is meaningful here -
// Proceed-gating for this drawer is computed centrally in useManageDealDrawer instead.
const noopSetDisablePrimary = () => {};

const PERMANENT_SAME_AS_CURRENT_OPTIONS = [
    { id: LABELS.yes, label: LABELS.yes },
    { id: LABELS.no, label: LABELS.no },
];

// Clones a field's backend config with a swapped label - used for the Permanent Address
// Ownership dropdown, which reuses Current Address Ownership's dropdownOptions but has no
// config entry of its own.
const cloneConfigWithLabel = (config: any, label: string) => (config ? { ...config, label } : null);

// Overrides `readonly` on a field's config for a specific edit state (Name/PAN are locked once
// this row is linked to an existing director - see coApplicantTypes.resolveApplicantEditState).
const withReadonlyOverride = (config: any, readonly: boolean) => (readonly ? { ...config, readonly: true } : config);

export const ApplicantFieldsBlock: React.FC<ApplicantFieldsBlockProps> = ({
    sectionKey,
    applicantConfig,
    formData,
    handleChange,
    directorId,
    managementProfileId,
    linkSource,
    onRemove,
    ...formSectionPassThroughProps
}) => {
    const editState = resolveApplicantEditState({ directorId, managementProfileId, linkSource });
    const readOnlyFieldKeys = READ_ONLY_FIELD_KEYS_BY_EDIT_STATE[editState];
    const isReadonly = !!formSectionPassThroughProps.dealDrawerState?.readonly;

    // Ephemeral UI-only collapse state - never persisted, never read by the save/validation
    // logic (which reads formData/coApplicants, not the DOM). Default expanded.
    const [isExpanded, setIsExpanded] = useState(true);
    const toggleExpanded = () => setIsExpanded((prev) => !prev);
    // The collapsed body is unmounted, so a keyboard-only user must be able to re-expand it -
    // make the header operable via Enter/Space, not just a mouse click.
    const handleHeaderKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            toggleExpanded();
        }
    };
    const handleRemoveClick = () => {
        onRemove?.();
    };

    const panFieldIndex = MANDATE_APPLICANT_FIELD_ORDER.indexOf(FIELD_KEYS.PAN);
    const buildFieldsConfig = (fieldKeys: string[]): Record<string, any> => {
        const config: Record<string, any> = {};
        fieldKeys.forEach((fieldKey) => {
            if (!applicantConfig?.[fieldKey]) return;
            config[fieldKey] = withReadonlyOverride(applicantConfig[fieldKey], readOnlyFieldKeys.includes(fieldKey));
        });
        return config;
    };
    const fieldsUptoPanConfig = buildFieldsConfig(MANDATE_APPLICANT_FIELD_ORDER.slice(0, panFieldIndex + 1));
    const fieldsAfterPanConfig = buildFieldsConfig(MANDATE_APPLICANT_FIELD_ORDER.slice(panFieldIndex + 1));

    const permanentAddressConfig = applicantConfig?.[FIELD_KEYS.PERMANENT_ADDRESS];
    const currentAddressOwnershipConfig = applicantConfig?.[FIELD_KEYS.CURRENT_ADDRESS_OWNERSHIP];
    const permanentAddressOwnershipConfig = cloneConfigWithLabel(currentAddressOwnershipConfig, LABELS.permanentAddressOwnershipLabel);

    const permanentSameAsCurrentKey = `${sectionKey}.${FIELD_KEYS.PERMANENT_SAME_AS_CURRENT}`;
    const permanentSameAsCurrent = !!formData[permanentSameAsCurrentKey];

    if (!applicantConfig) return null;

    const fieldKey = (key: string) => `${sectionKey}.${key}`;
    const nameValue = formData[fieldKey(FIELD_KEYS.NAME)] || '';
    const phoneKey = fieldKey(FIELD_KEYS.PHONE);
    const emailKey = fieldKey(FIELD_KEYS.EMAIL);
    const currentAddressPincodeKey = fieldKey(FIELD_KEYS.CURRENT_ADDRESS_PINCODE);
    const phoneValue = formData[phoneKey] || '';
    const emailValue = formData[emailKey] || '';
    const currentAddressPincodeValue = formData[currentAddressPincodeKey] || '';
    const applicantTypeKey = fieldKey(MANDATE_APPLICANT_TYPE_KEY);
    const applicantTypeValue = formData[applicantTypeKey] || '';
    const isPhoneReadonly = isReadonly || readOnlyFieldKeys.includes(FIELD_KEYS.PHONE);
    const isPhoneValid = isApplicantPhoneValid(phoneValue);
    const isEmailValid = isApplicantEmailValid(emailValue);
    const isPincodeValid = isApplicantPincodeValid(currentAddressPincodeValue);

    return (
        <div className={styles.ApplicantFieldsBlockWrapper}>
            <div className={styles.ApplicantCardHeader}>
                <div
                    className={styles.ApplicantCardHeaderTitle}
                    onClick={toggleExpanded}
                    onKeyDown={handleHeaderKeyDown}
                    role="button"
                    tabIndex={0}
                    aria-expanded={isExpanded}
                >
                    <img
                        src={BLACK_DROPDOWN_CARET}
                        alt=""
                        className={clsx(styles.ApplicantCardCaret, { [styles.ApplicantCardCaretExpanded]: isExpanded })}
                    />
                    <img src={user_outline} alt="" className={styles.ApplicantCardPersonIcon} />
                    <span className={styles.ApplicantCardName}>{nameValue || LABELS.newApplicantHeader}</span>
                </div>
                {onRemove && !isReadonly && (
                    <Button
                        type="button"
                        variant="tertiary"
                        text={MANDATE_APPLICANT_REMOVE_LABEL}
                        className={styles.PecTextButton}
                        onClick={handleRemoveClick}
                    />
                )}
            </div>

            {isExpanded && (
                <div className={styles.ApplicantCardBody}>
                    <div className={styles.ApplicantContactFieldRow}>
                        <div className={clsx(styles.InputLabel, styles.InputItemLabel)}>{LABELS.applicantTypeLabel}</div>
                        <div className={styles.FieldFullWidthWrapper}>
                            <Dropdown
                                selectedOption={applicantTypeValue ? { id: applicantTypeValue, label: applicantTypeValue } : null}
                                optionsList={MANDATE_APPLICANT_TYPE_OPTIONS}
                                placeholder={LABELS.applicantTypePlaceholder}
                                itemClickHandler={(option) => handleChange(applicantTypeKey, option.id)}
                                style={{ width: '100%', height: '44px' }}
                                disabled={isReadonly}
                                showFloatingLabel={false}
                                hideClearIcon
                            />
                        </div>
                    </div>

                    <div className={styles.ApplicantMainFieldsWrapper}>
                        <FormSection
                            section={{ sectionKey, config: fieldsUptoPanConfig }}
                            formData={formData}
                            handleChange={handleChange}
                            setDisablePrimary={noopSetDisablePrimary}
                            hideSectionHeading
                            step={0}
                            sectionIndex={0}
                            totalSteps={0}
                            {...formSectionPassThroughProps}
                        />

                        <div className={styles.ApplicantContactFieldRow}>
                            <div className={clsx(styles.InputLabel, styles.InputItemLabel)}>{LABELS.phoneLabel}</div>
                            <div className={styles.PhoneNumberInputWrap}>
                                <span className={styles.PhoneNumberPrefix}>{MANDATE_APPLICANT_PHONE_COUNTRY_CODE_PREFIX}</span>
                                <Input
                                    type="number"
                                    placeholder={LABELS.phonePlaceholder}
                                    value={phoneValue}
                                    onChange={(value: string) =>
                                        handleChange(phoneKey, numberOnly(value).slice(0, MANDATE_APPLICANT_PHONE_DIGIT_LENGTH))
                                    }
                                    errorMessage={LABELS.invalidPhoneMessage}
                                    isValid={isPhoneValid}
                                    disabled={isPhoneReadonly}
                                    showFloatingLabel={false}
                                    inputClassName={styles.PhoneNumberInputField}
                                    containerClassName={styles.FieldControlSm}
                                />
                            </div>
                        </div>

                        <div className={styles.ApplicantContactFieldRow}>
                            <div className={clsx(styles.InputLabel, styles.InputItemLabel)}>{LABELS.emailLabel}</div>
                            <div className={styles.FieldFullWidthWrapper}>
                                <Input
                                    type="email"
                                    placeholder={LABELS.emailPlaceholder}
                                    value={emailValue}
                                    onChange={(value: string) => handleChange(emailKey, value)}
                                    errorMessage={LABELS.invalidEmailMessage}
                                    isValid={isEmailValid}
                                    disabled={isReadonly}
                                    allowAllCharacters
                                    showFloatingLabel={false}
                                    containerClassName={styles.FieldControlSm}
                                />
                            </div>
                        </div>

                        <FormSection
                            section={{ sectionKey, config: fieldsAfterPanConfig }}
                            formData={formData}
                            handleChange={handleChange}
                            setDisablePrimary={noopSetDisablePrimary}
                            hideSectionHeading
                            step={0}
                            sectionIndex={0}
                            totalSteps={0}
                            {...formSectionPassThroughProps}
                        />

                        <div className={styles.ApplicantContactFieldRow}>
                            <div className={clsx(styles.InputLabel, styles.InputItemLabel)}>{LABELS.currentAddressPincodeLabel}</div>
                            <div className={styles.FieldFullWidthWrapper}>
                                <Input
                                    type="number"
                                    placeholder={LABELS.currentAddressPincodePlaceholder}
                                    value={currentAddressPincodeValue}
                                    onChange={(value: string) =>
                                        handleChange(currentAddressPincodeKey, numberOnly(value).slice(0, PINCODE_DIGIT_LENGTH))
                                    }
                                    errorMessage={LABELS.invalidPincodeMessage}
                                    isValid={isPincodeValid}
                                    disabled={isReadonly}
                                    showFloatingLabel={false}
                                    containerClassName={styles.FieldControlSm}
                                />
                            </div>
                        </div>
                    </div>

                    {permanentAddressConfig && (
                        <div className={styles.InputList}>
                            <div className={clsx(styles.InputItem, styles.PermanentSameAsCurrentRow)}>
                                <div className={clsx(styles.InputLabel, styles.InputItemLabel)}>{LABELS.permanentSameAsCurrent}</div>
                                <div className={styles.FieldFullWidthWrapper}>
                                    <Dropdown
                                        selectedOption={{
                                            id: permanentSameAsCurrent ? LABELS.yes : LABELS.no,
                                            label: permanentSameAsCurrent ? LABELS.yes : LABELS.no,
                                        }}
                                        optionsList={PERMANENT_SAME_AS_CURRENT_OPTIONS}
                                        itemClickHandler={(option) => handleChange(permanentSameAsCurrentKey, option.id === LABELS.yes)}
                                        style={{ width: '100%', height: '44px' }}
                                        disabled={isReadonly}
                                        showFloatingLabel={false}
                                        hideClearIcon
                                    />
                                </div>
                            </div>
                        </div>
                    )}

                    {permanentAddressConfig && !permanentSameAsCurrent && (
                        <FormSection
                            section={{
                                sectionKey,
                                config: {
                                    [FIELD_KEYS.PERMANENT_ADDRESS]: permanentAddressConfig,
                                    ...(permanentAddressOwnershipConfig
                                        ? { [FIELD_KEYS.PERMANENT_ADDRESS_OWNERSHIP]: permanentAddressOwnershipConfig }
                                        : {}),
                                },
                            }}
                            formData={formData}
                            handleChange={handleChange}
                            setDisablePrimary={noopSetDisablePrimary}
                            hideSectionHeading
                            step={0}
                            sectionIndex={0}
                            totalSteps={0}
                            {...formSectionPassThroughProps}
                        />
                    )}
                </div>
            )}
        </div>
    );
};

export default ApplicantFieldsBlock;
